/**
 * Class Inline Tabs
 */
class VerticalTabs {
	element: HTMLElement;
	triggers: NodeListOf<Element>;
	mobileTriggers: NodeListOf<Element>;
	indicator: HTMLElement | null;

	constructor(element: HTMLElement) {
		this.element = element;
		this.triggers = this.element.querySelectorAll('.block-vertical-tabs__desktop-tab');
		this.mobileTriggers = this.element.querySelectorAll('.block-vertical-tabs-bellow__button');
		this.indicator = this.element.querySelector('.block-vertical-tabs__indicator');
		this.initTabs();
		this.initIndicator();
		this.handleResize();
	}

	/**
	 * Iniitializes the event listeners for the tab triggers.
	 */
	private initTabs(): void {
		this.triggers.forEach((trigger, index) => {
			if (index === 0 && trigger instanceof HTMLElement) {
				this.activateTab(trigger);
			}
			trigger.addEventListener('click', this.handleClick.bind(this));
		});
		this.mobileTriggers.forEach((trigger) => {
			trigger.addEventListener('click', this.handleClick.bind(this));
		});
	}

	/**
	 * Initializes the indicator element.
	 */
	private initIndicator(): void {
		if (this.element && this.indicator) {
			const tabElement = this.element.querySelector(
				'.block-vertical-tabs__desktop-tab'
			) as HTMLElement | null;

			if (tabElement) {
				const buttonHeight = tabElement.offsetHeight;
				this.indicator.style.height = `${buttonHeight}px`;
			}
		}
	}

	/**
	 * Handles the click event on a tab trigger.
	 * @param event - The click event.
	 */
	private handleClick(event: Event): void {
		const target = event.currentTarget as HTMLElement;
		const isMobileTrigger = target.closest('.block-vertical-tabs-bellow__button');
		const ariaExpanded = target.getAttribute('aria-expanded');

		if (isMobileTrigger) {
			this.resetTabs(this.mobileTriggers);
			if (ariaExpanded === 'false') {
				this.activateTab(target);
			} else {
				this.deactivateTab(target);
			}
		} else {
			this.resetTabs(this.triggers);
			this.activateTab(target);
			this.moveIndicator(target);
		}
	}

	/**
	 * Resets all tabs to their default state.
	 * @param triggers - The tab triggers to reset.
	 */
	private resetTabs(triggers: NodeListOf<Element>): void {
		triggers.forEach((trigger) => {
			this.deactivateTab(trigger as HTMLElement);
		});
	}

	/**
	 * Activates the clicked tab and its associated content.
	 * @param trigger - The clicked tab trigger.
	 */
	private activateTab(trigger: HTMLElement): void {
		trigger.setAttribute('aria-expanded', 'true');
		const contentId = trigger.getAttribute('aria-controls');
		if (contentId) {
			const content = document.getElementById(contentId);
			if (content) {
				if (window.matchMedia('(max-width: 767px)').matches) {
					$(content).slideDown(300, () => {
						content.setAttribute('aria-expanded', 'true');
					});
				} else {
					content.setAttribute('aria-expanded', 'true');
				}
			}
		}
	}

	private deactivateTab(trigger: HTMLElement): void {
		trigger.setAttribute('aria-expanded', 'false');
		const contentId = trigger.getAttribute('aria-controls');
		if (contentId) {
			const content = document.getElementById(contentId);
			if (content) {
				if (window.matchMedia('(max-width: 767px)').matches) {
					$(content).slideUp(300, () => {
						content.setAttribute('aria-expanded', 'false');
					});
				} else {
					content.setAttribute('aria-expanded', 'false');
				}
			}
		}
	}

	/**
	 * Moves the indicator element to the clicked tab.
	 * @param trigger - The clicked tab trigger.
	 */
	private moveIndicator(trigger: HTMLElement): void {
		if (this.indicator) {
			const offsetTop = trigger.offsetTop;
			const buttonHeight = trigger.offsetHeight;
			this.indicator.style.top = `${offsetTop}px`;
			this.indicator.style.height = `${buttonHeight}px`;
		}
	}

	/**
	 * Handle window resize and recalculate the indicator position and height.
	 */
	private handleResize(): void {
		window.addEventListener('resize', () => {
			const activeTab = this.element.querySelector(
				'.block-vertical-tabs__desktop-tab[aria-expanded="true"]'
			) as HTMLElement | null;

			if (activeTab) {
				// Adjust the indicator height and position on resize
				this.moveIndicator(activeTab);
			}
		});
	}
}

document.addEventListener('DOMContentLoaded', () => {
	document.querySelectorAll('.block-vertical-tabs').forEach((element) => {
		new VerticalTabs(element as HTMLElement);
	});
});
